// src/core/Modals.js
import React, { useState } from 'react';
import { XCircle } from 'lucide-react';
import { getPhenomenonExplanation, toPersianDigits } from './utils';

// ExplanationModal (for phenomenon description)
export const ExplanationModal = ({ phenomenon, onClose }) => {
  const explanation = getPhenomenonExplanation(phenomenon.name);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white p-8 rounded-lg shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto" dir="rtl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-blue-700">توضیحات پدیده: {phenomenon.name}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <XCircle size={24} />
          </button>
        </div>
        <p className="text-gray-700 leading-relaxed text-justify">{explanation}</p>
        <div className="mt-6 text-center">
          <button
            onClick={onClose}
            className="bg-blue-700 hover:bg-blue-800 text-white font-semibold py-2 px-6 rounded-lg shadow-md transition duration-300 ease-in-out"
          >
            بستن
          </button>
        </div>
      </div>
    </div>
  );
};

// GuideModal (for general instructions)
export const GuideModal = ({ onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white p-8 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" dir="rtl">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-blue-700 text-center flex-grow">راه‌نمای تکمیل فرم</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <XCircle size={24} />
          </button>
        </div>
        <p className="mb-4 text-justify leading-relaxed">
          این سامانه با هدف جمع‌آوری تحلیل‌های عمیق و راهکارهای عملی شما در خصوص تأثیر پدیده‌های گوناگون بر سازمان تأمین اجتماعی طراحی شده است. اطلاعات ارزشمند شما به عنوان اسناد پشتیبان برای تدوین سند چشم‌انداز ۱۴۳۰ مورد استفاده قرار خواهد گرفت. لطفاً با دقت و جزئیات کافی به پرسش‌ها پاسخ دهید.
        </p>

        <h3 className="text-xl font-semibold mb-3 text-blue-600">گردش کار کلی:</h3>
        <ul className="list-disc list-inside mb-4 space-y-2 text-justify">
            <li><span className="font-medium">ورود:</span> با استفاده از کد ملی خود وارد سامانه شوید. اگر کاربر جدید هستید، به صفحه تکمیل اطلاعات هدایت می‌شوید.</li>
            <li><span className="font-medium">تکمیل اطلاعات کارشناس:</span> در اولین ورود، فرم اطلاعات فردی و تخصصی خود را تکمیل و ذخیره کنید. در مراجعات بعدی می‌توانید این اطلاعات را ویرایش نمایید.</li>
            <li><span className="font-medium">تحلیل پدیده‌ها:</span> از فهرست سمت راست، پدیده مورد نظر را انتخاب و به سؤالات مربوط به آن در پنل اصلی پاسخ دهید.</li>
            <li><span className="font-medium">ذخیره و ارسال:</span> پاسخ‌های شما به محض تغییر به صورت خودکار ذخیره می‌شوند. در پایان کار، حتماً روی دکمه «ارسال» در بالای صفحه کلیک کنید تا اطلاعات شما نهایی شود.</li>
        </ul>

        <h3 className="text-xl font-semibold mb-3 text-blue-600">آشنایی با صفحه اصلی:</h3>
        <ul className="list-disc list-inside mb-4 space-y-2 text-justify">
            <li><span className="font-medium">نوار پیشرفت کلی:</span> در بالای صفحه، میانگین درصد تکمیل همه پدیده‌ها را مشاهده می‌کنید.</li>
            <li><span className="font-medium">فهرست پدیده‌ها (منوی راست):</span> شامل دو بخش اصلی "اطلاعات کارشناس" و "تحلیل پدیده‌ها" است.</li>
            <li><span className="font-medium">جستجو:</span> می‌توانید پدیده مورد نظر خود را در فهرست جستجو کنید.</li>
            <li><span className="font-medium">افزودن پدیده جدید:</span> با کلیک بر دکمه «+ افزودن پدیده پیشنهادی جدید»، می‌توانید پدیده‌ای که در لیست نیست را اضافه و تحلیل کنید.</li>
            <li><span className="font-medium">حذف پدیده پیشنهادی:</span> کنار پدیده‌هایی که خودتان اضافه کرده‌اید، یک آیکون سطل زباله برای حذف وجود دارد.</li>
            <li><span className="font-medium">تکمیل و ذخیره‌سازی:</span> پاسخ‌های شما به محض ورود اطلاعات، به صورت خودکار در سیستم ذخیره می‌شوند. دکمه «ذخیره» در پایین فرم نیز همین کار را انجام می‌دهد. دکمه «ارسال» در بالای صفحه برای اعلام اتمام نهایی کار شماست.</li>
        </ul>

        <h3 className="text-xl font-semibold mb-3 text-blue-600">تعاریف مقیاس‌ها و اصطلاحات:</h3>
        <ul className="list-disc list-inside mb-4 space-y-2 text-justify">
          <li><span className="font-medium">افق زمانی تأثیر:</span> **کوتاه‌مدت:** ۱-۳ سال، **میان‌مدت:** ۳-۷ سال، **بلندمدت:** ۷-۱۵ سال.</li>
          <li><span className="font-medium">اهمیت و اولویت (مقیاس ۰ تا ۱۰):</span> درجه‌بندی از ۰ (نظری ندارم) تا ۱۰ (بیشترین اهمیت).</li>
          <li>
            <span className="font-medium">شدت تأثیر (مقیاس عمومی ۰ تا ۵):</span>
            <ul className="list-circle list-inside mr-4">
              <li><strong style={{ color: '#ef4444' }}>۰: نظری ندارم</strong> (دکمه شرح... غیرفعال)</li>
              <li><strong style={{ color: '#f97316' }}>۱: هیچ</strong> (دکمه شرح... غیرفعال)</li>
              <li><strong style={{ color: '#facc15' }}>۲: کم</strong></li>
              <li><strong style={{ color: '#84cc16' }}>۳: متوسط</strong></li>
              <li><strong style={{ color: '#22c55e' }}>۴: زیاد</strong></li>
              <li><strong style={{ color: '#15803d' }}>۵: بسیار زیاد</strong></li>
            </ul>
          </li>
           <li>
            <span className="font-medium">شدت تأثیر بر منابع/مصارف (مقیاس کاهشی/افزایشی):</span>
            <ul className="list-circle list-inside mr-4">
              <li><strong style={{ color: '#ef4444' }}>۰: نظری ندارم</strong> (دکمه شرح... غیرفعال)</li>
              <li><strong style={{ color: '#f97316' }}>۱: خیلی کاهش می‌دهد</strong></li>
              <li><strong style={{ color: '#facc15' }}>۲: کاهش می‌دهد</strong></li>
              <li><strong style={{ color: '#84cc16' }}>۳: بی‌اثر</strong> (دکمه شرح... غیرفعال)</li>
              <li><strong style={{ color: '#22c55e' }}>۴: افزایش می‌دهد</strong></li>
              <li><strong style={{ color: '#15803d' }}>۵: خیلی افزایش می‌دهد</strong></li>
            </ul>
          </li>
           <li><span className="font-medium">دکمه «شرح...»:</span> برای اکثر سؤالات اسلایدری، پس از انتخاب گزینه‌ای به جز موارد خنثی (مانند نظری ندارم، هیچ، بی‌اثر)، دکمه «شرح...» ظاهر می‌شود. با کلیک بر روی این دکمه (یا روی خود عنوان سؤال) می‌توانید توضیح کیفی و علت انتخاب خود را وارد کنید. **ارائه این توضیحات بسیار ارزشمند است.**</li>
        </ul>

        <h3 className="text-xl font-semibold mb-3 text-blue-600">راهنمای بخش‌های فرم تحلیل پدیده:</h3>
        <ul className="list-decimal list-inside mb-4 space-y-3 text-justify">
          <li><span className="font-medium">ارزیابی کلی:</span> در این بخش تأثیر کلی، افق زمانی، اولویت و ارتباط یک پدیده با سایر پدیده‌ها را مشخص می‌کنید. پس از انتخاب پدیده‌های مرتبط، دکمه «شرح...» برای توضیح چگونگی این ارتباط ظاهر می‌شود.</li>
          <li><span className="font-medium">تحلیل و اثرسنجی:</span> مشخص می‌کنید که پدیده مورد نظر برای سازمان یک فرصت، بحران، ظرفیت، پیشران یا یک تغییر تحول‌آفرین است.</li>
          <li><span className="font-medium">تأثیر بر بخش‌های سازمان:</span> تأثیر پدیده را بر سه حوزه اصلی **بیمه‌ای، درمان و سرمایه‌گذاری** و همچنین بر **سایر بخش‌های کلان** (مانند نحوه اداره، ساختار، منابع انسانی و...) ارزیابی می‌کنید. برای بخش‌های کلان، پس از تیک زدن هر مورد، دکمه «شرح...» برای ارائه توضیحات فعال می‌شود.</li>
          <li><span className="font-medium">تأثیر بر اهداف کلان:</span> تأثیر پدیده را بر اهداف کلان شش‌گانه سازمان (فراگیری پوشش، جامعیت خدمات و...) مشخص می‌کنید.</li>
          <li><span className="font-medium">راهکارها و ایده‌ها:</span> ایده عملیاتی خود را برای مواجهه با پدیده شرح داده و سپس آن را بر اساس ۶ معیار ارزیابی می‌کنید.</li>
          <li><span className="font-medium">ابرپروژه:</span> مشخص می‌کنید که آیا ایده شما قابلیت تبدیل شدن به یک پروژه کلان را دارد یا خیر.</li>
          <li><span className="font-medium">الزامات:</span> پیش‌نیازها و مقدمات لازم برای اجرای ایده پیشنهادی خود را در این بخش شرح می‌دهید.</li>
          <li><span className="font-medium">سایر توضیحات:</span> هر نکته تکمیلی دیگری که لازم می‌دانید در این کادر وارد کنید.</li>
        </ul>

        <div className="mt-6 text-center">
          <button
            onClick={onClose}
            className="bg-blue-700 hover:bg-blue-800 text-white font-semibold py-2 px-6 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105"
          >
            متوجه شدم، شروع می‌کنم!
          </button>
        </div>
      </div>
    </div>
  );
};


// A simple helper to render text with bold formatting (for **text**)
const renderTextWithBold = (text) => {
  if (!text) return null;
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

// WelcomeModal (for initial greeting)
export const WelcomeModal = ({ onClose, message }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white p-8 rounded-lg shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto" dir="rtl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-blue-700">همکار و اندیشمند گرامی</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <XCircle size={24} />
          </button>
        </div>
        {message.map((block, index) => {
          if (block.type === 'paragraph') {
            return (
              <p key={index} className="text-gray-700 leading-relaxed text-justify mb-2">
                {renderTextWithBold(block.content)}
              </p>
            );
          } else if (block.type === 'divider') {
            return <hr key={index} className="my-4 border-gray-300" />;
          } else if (block.type === 'heading' && block.level === 3) {
            return (
              <h3 key={index} className="text-xl font-semibold mb-3 text-blue-600">
                {renderTextWithBold(block.content)}
              </h3>
            );
          } else if (block.type === 'list') {
            return (
              <ul key={index} className="list-disc list-inside mb-4 space-y-2 text-justify">
                {block.items.map((item, itemIndex) => (
                  <li key={itemIndex}>
                    {renderTextWithBold(item.content)}
                    {item.email && <a href={`mailto:${item.email}`} className="text-blue-600 hover:underline ltr-link">{item.email}</a>}
                  </li>
                ))}
              </ul>
            );
          }
          return null;
        })}
        <div className="mt-6 text-center">
          <button
            onClick={onClose}
            className="bg-blue-700 hover:bg-blue-800 text-white font-semibold py-2 px-6 rounded-lg shadow-md transition duration-300 ease-in-out"
          >
            ورود
          </button>
        </div>
      </div>
    </div>
  );
};

// Generic Field Explanation Modal
export const FieldExplanationModal = ({ titleComponent, initialExplanation, onSave, onClose }) => {
  const [explanation, setExplanation] = useState(initialExplanation || '');
  const charCount = explanation.length;

  const handleSave = () => {
    onSave(explanation);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white p-8 rounded-lg shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto" dir="rtl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-blue-700 flex-grow text-right">{titleComponent}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <XCircle size={24} />
          </button>
        </div>
        <div className="mb-4">
          <textarea
            rows="6"
            value={explanation}
            onChange={(e) => setExplanation(e.target.value)}
            className="w-full p-3 border rounded-lg focus:ring-blue-500 focus:border-blue-500 text-right border-gray-300"
            placeholder="شرح خود را اینجا وارد کنید..."
          ></textarea>
          <div className="text-xs text-gray-500 mt-1 flex justify-between">
            <span>{`تعداد کاراکتر: ${toPersianDigits(charCount)}`}</span>
          </div>
        </div>
        <div className="mt-6 text-center flex justify-center space-x-4 space-x-reverse">
          <button
            onClick={handleSave}
            className="bg-blue-700 hover:bg-blue-800 text-white font-semibold py-2 px-6 rounded-lg shadow-md transition duration-300 ease-in-out"
          >
            ذخیره
          </button>
          <button
            onClick={onClose}
            className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-6 rounded-lg shadow-md transition duration-300 ease-in-out"
          >
            بستن
          </button>
        </div>
      </div>
    </div>
  );
};

// InterdependencyExplanationInputModal (for explaining interdependencies)
export const InterdependencyExplanationInputModal = ({ currentPhenomenonName, selectedInterdependencyNames, initialExplanation, onSave, onClose }) => {
  const [explanation, setExplanation] = useState(initialExplanation || '');

  const formattedInterdependencies = selectedInterdependencyNames.map((name, index) => (
    <React.Fragment key={index}>
      <span className="text-purple-700 font-semibold">{name}</span>
      {index < selectedInterdependencyNames.length - 1 && '، '}
    </React.Fragment>
  ));

  const modalTitle = (
    <>
      لطفاً شرح دهید پدیده "<span className="text-blue-700">{currentPhenomenonName}</span>" با پدیده‌های {formattedInterdependencies} چگونه ارتباط معنادار دارد؟
    </>
  );
  const charCount = explanation.length;

  const handleSave = () => {
    onSave(explanation);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white p-8 rounded-lg shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto" dir="rtl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-blue-700 flex-grow text-right">{modalTitle}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <XCircle size={24} />
          </button>
        </div>
        <div className="mb-4">
          <textarea
            rows="6"
            value={explanation}
            onChange={(e) => setExplanation(e.target.value)}
            className="w-full p-3 border rounded-lg focus:ring-blue-500 focus:border-blue-500 text-right border-gray-300"
            placeholder="شرح خود را اینجا وارد کنید..."
          ></textarea>
          <div className="text-xs text-gray-500 mt-1 flex justify-between">
            <span>{`تعداد کاراکتر: ${toPersianDigits(charCount)}`}</span>
          </div>
        </div>
        <div className="mt-6 text-center flex justify-center space-x-4 space-x-reverse">
          <button
            onClick={handleSave}
            className="bg-blue-700 hover:bg-blue-800 text-white font-semibold py-2 px-6 rounded-lg shadow-md transition duration-300 ease-in-out"
          >
            ذخیره
          </button>
          <button
            onClick={onClose}
            className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-6 rounded-lg shadow-md transition duration-300 ease-in-out"
          >
            بستن
          </button>
        </div>
      </div>
    </div>
  );
};
